"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { ApiWaveCreditScope } from "@/generated/models/ApiWaveCreditScope";
import { ApiWaveCreditType } from "@/generated/models/ApiWaveCreditType";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import type { ApiWaveCreditNft } from "@/generated/models/ApiWaveCreditNft";
import { CREATE_WAVE_VALIDATION_ERROR } from "@/helpers/waves/create-wave.validation";
import { WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import CommonBorderedRadioButton from "@/components/utils/radio/CommonBorderedRadioButton";
import CreateWaveApprovalHold, { CreateWaveApprovalHoldMode, getCreateWaveApprovalHoldMode } from "./CreateWaveApprovalHold";
import CreateWaveVotingRep from "./CreateWaveVotingRep";
import CreateWaveVotingThreshold from "./CreateWaveVotingThreshold";
import CreateWaveVotingThresholdTime from "./CreateWaveVotingThresholdTime";
import MemeCardSetPicker from "./MemeCardSetPicker";
import MaxVotesPerIdentityInput from "./MaxVotesPerIdentityInput";
import NegativeVotingToggle from "./NegativeVotingToggle";
import TimeWeightedVoting from "./TimeWeightedVoting";
import type { TimeWeightedVotingConfig } from "./types";
const VOTING_TYPES_ORDER: Record<ApiWaveCreditType, number | undefined> = {
  [ApiWaveCreditType.TdhPlusXtdh]: 0,
  [ApiWaveCreditType.Tdh]: 1,
  [ApiWaveCreditType.Rep]: 2,
  [ApiWaveCreditType.CardSetTdh]: 3,
  [ApiWaveCreditType.Xtdh]: undefined
};
const TIME_WEIGHTED_DURATION_ERROR = "This interval is longer than the wave duration. Choose a shorter interval, extend the wave end date, or clear the end date.";
const APPROVAL_THRESHOLD_TIME_INVALID_ERROR = "Enter a whole number greater than 0, or leave blank for immediate approval.";
const APPROVAL_THRESHOLD_TIME_DURATION_ERROR = "This time is longer than the wave duration. Choose a shorter time, extend the wave end date, or clear the end date.";
const DEFAULT_APPROVAL_THRESHOLD_TIME_MS = 60 * 1000;
const VOTING_SETTINGS_GRID_CLASSES = "tw-mt-6 tw-grid tw-grid-cols-1 tw-gap-3 tw-border-t tw-border-iron-700 tw-pt-6";
const VOTING_OPTIONS_GRID_CLASSES = "tw-mt-3 tw-grid tw-gap-3 sm:tw-grid-cols-2 lg:tw-grid-cols-4 [&>div]:tw-gap-x-2 [&>div]:tw-px-3 [&>div]:tw-py-3";
const CREDIT_SCOPE_OPTIONS: readonly {
  readonly scope: ApiWaveCreditScope;
  readonly label: string;
  readonly description: string;
}[] = [{
  scope: ApiWaveCreditScope.Wave,
  label: "Whole wave",
  description: "Each identity has one voting budget across the wave."
}, {
  scope: ApiWaveCreditScope.Drop,
  label: "Each drop",
  description: "Voting power applies separately to every drop."
}];
const getCreateWaveVotingLabel = (votingType: ApiWaveCreditType): string => {
  if (votingType === ApiWaveCreditType.CardSetTdh) {
    return "Memes TDH";
  }
  return WAVE_VOTING_LABELS[votingType];
};
const getApprovalThresholdTimeErrorMessage = (errors: CREATE_WAVE_VALIDATION_ERROR[]): string | undefined => {
  if (errors.includes(CREATE_WAVE_VALIDATION_ERROR.APPROVAL_THRESHOLD_TIME_EXCEEDS_WAVE_DURATION)) {
    return APPROVAL_THRESHOLD_TIME_DURATION_ERROR;
  }
  if (errors.includes(CREATE_WAVE_VALIDATION_ERROR.APPROVAL_THRESHOLD_TIME_INVALID)) {
    return APPROVAL_THRESHOLD_TIME_INVALID_ERROR;
  }
  return undefined;
};
function CreateWaveCreditScopeSelect(t0) {
  const $ = _c(6);
  const {
    creditScope,
    onCreditScopeChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <legend className="tw-mb-3 tw-block tw-text-sm tw-font-semibold tw-text-iron-100">Voting power scope</legend>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== creditScope || $[2] !== onCreditScopeChange) {
    t2 = CREDIT_SCOPE_OPTIONS.map(option => {
      const selected = creditScope === option.scope;
      return <label key={option.scope} className={`tw-flex tw-cursor-pointer tw-gap-x-3 tw-rounded-xl tw-border tw-border-solid tw-p-4 tw-ring-1 tw-ring-inset tw-transition tw-duration-300 tw-ease-out ${selected ? "tw-border-primary-400 tw-bg-primary-500/5 tw-ring-primary-500/30" : "tw-border-white/5 tw-bg-iron-900 tw-ring-white/5 hover:tw-border-white/10 hover:tw-bg-iron-800 hover:tw-ring-white/10"}`}><input type="radio" name="create-wave-credit-scope" value={option.scope} checked={selected} onChange={() => onCreditScopeChange(option.scope)} className="tw-mt-1 tw-form-radio tw-h-4 tw-w-4 tw-cursor-pointer tw-border tw-border-solid tw-border-iron-650 tw-bg-iron-800 tw-text-primary-400 tw-ring-offset-iron-800 tw-transition tw-duration-300 tw-ease-out focus:tw-ring-2 focus:tw-ring-primary-400" /><span className="tw-min-w-0"><span className={`tw-block tw-text-sm tw-font-semibold ${selected ? "tw-text-primary-400" : "tw-text-iron-200"}`}>{option.label}</span><span className="tw-mt-1 tw-block tw-text-xs tw-font-medium tw-leading-5 tw-text-iron-400">{option.description}</span></span></label>;
    });
    $[1] = creditScope;
    $[2] = onCreditScopeChange;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <fieldset className="tw-mt-6 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-700 tw-px-0 tw-pb-0 tw-pt-6">{t1}<div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2">{t2}</div></fieldset>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
export default function CreateWaveVoting(t0) {
  const $ = _c(75);
  const {
    waveType,
    selectedType,
    category,
    profileId,
    creditNfts,
    creditScope,
    memeCount,
    isMemeCountLoading,
    isMemeCountError,
    allowNegativeVotes,
    maxVotesPerIdentityPerDrop,
    approvalThreshold,
    approvalThresholdTimeMs,
    errors,
    onTypeChange,
    setCategory,
    setProfileId,
    setCreditNfts,
    onCreditScopeChange,
    onAllowNegativeVotesChange,
    setMaxVotesPerIdentityPerDrop,
    setApprovalThreshold,
    setApprovalThresholdTimeMs,
    timeWeighted,
    onTimeWeightedChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      [ApiWaveType.Chat]: "How Drops are Rated",
      [ApiWaveType.Rank]: "How Drops are Voted",
      [ApiWaveType.Approve]: "How Drops are Voted"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const TITLES = t1;
  const [approvalHoldModeOverride, setApprovalHoldModeOverride] = useState(null);
  if (selectedType === null) {
    return null;
  }
  const timeWeightedErrorMessage = errors.includes(CREATE_WAVE_VALIDATION_ERROR.TIME_WEIGHTED_VOTING_INTERVAL_EXCEEDS_WAVE_DURATION) ? TIME_WEIGHTED_DURATION_ERROR : undefined;
  let t2;
  if ($[1] !== errors) {
    t2 = errors.includes(CREATE_WAVE_VALIDATION_ERROR.APPROVAL_THRESHOLD_REQUIRED);
    $[1] = errors;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const approvalThresholdError = t2;
  let t3;
  if ($[3] !== errors) {
    t3 = getApprovalThresholdTimeErrorMessage(errors);
    $[3] = errors;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const approvalThresholdTimeErrorMessage = t3;
  const showVotingSettings = waveType !== ApiWaveType.Chat;
  let t4;
  if ($[5] !== approvalThresholdTimeMs) {
    t4 = getCreateWaveApprovalHoldMode({
      thresholdTimeMs: approvalThresholdTimeMs
    });
    $[5] = approvalThresholdTimeMs;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const inferredApprovalHoldMode = t4;
  const approvalHoldMode = approvalHoldModeOverride ?? inferredApprovalHoldMode;
  let t5;
  if ($[7] !== approvalThresholdTimeMs || $[8] !== setApprovalThresholdTimeMs) {
    t5 = mode => {
      setApprovalHoldModeOverride(mode);
      bb13: switch (mode) {
        case CreateWaveApprovalHoldMode.NONE:
          {
            setApprovalThresholdTimeMs(null);
            break bb13;
          }
        case CreateWaveApprovalHoldMode.HOLD:
          {
            setApprovalThresholdTimeMs(approvalThresholdTimeMs !== null && Number.isFinite(approvalThresholdTimeMs) && approvalThresholdTimeMs > 0 ? approvalThresholdTimeMs : DEFAULT_APPROVAL_THRESHOLD_TIME_MS);
          }
      }
    };
    $[7] = approvalThresholdTimeMs;
    $[8] = setApprovalThresholdTimeMs;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const onApprovalHoldModeChange = t5;
  let t6;
  if ($[10] !== setApprovalThresholdTimeMs) {
    t6 = value => {
      setApprovalHoldModeOverride(CreateWaveApprovalHoldMode.HOLD);
      setApprovalThresholdTimeMs(value);
    };
    $[10] = setApprovalThresholdTimeMs;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const onApprovalThresholdTimeChange = t6;
  const t7 = TITLES[waveType];
  let t8;
  if ($[12] !== t7) {
    t8 = <p className="tw-mb-0 tw-text-lg tw-font-semibold tw-text-iron-50 sm:tw-text-xl">{t7}</p>;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== onTypeChange || $[15] !== selectedType) {
    t9 = (Object.keys(VOTING_TYPES_ORDER) as ApiWaveCreditType[]).filter(_temp).map(votingType_0 => <CommonBorderedRadioButton key={votingType_0} type={votingType_0} selected={selectedType} disabled={false} variant="subtle" onChange={onTypeChange}><span className={`tw-flex tw-min-h-4 tw-items-center tw-text-sm tw-font-semibold ${selectedType === votingType_0 ? "tw-text-white" : "tw-text-iron-300 group-hover:tw-text-white"}`}>{getCreateWaveVotingLabel(votingType_0)}</span></CommonBorderedRadioButton>);
    $[14] = onTypeChange;
    $[15] = selectedType;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== category || $[18] !== errors || $[19] !== profileId || $[20] !== selectedType || $[21] !== setCategory || $[22] !== setProfileId) {
    t10 = selectedType === ApiWaveCreditType.Rep && <div className="tw-col-span-full"><CreateWaveVotingRep category={category} profileId={profileId} errors={errors} setCategory={setCategory} setProfileId={setProfileId} /></div>;
    $[17] = category;
    $[18] = errors;
    $[19] = profileId;
    $[20] = selectedType;
    $[21] = setCategory;
    $[22] = setProfileId;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== creditNfts || $[25] !== errors || $[26] !== isMemeCountError || $[27] !== isMemeCountLoading || $[28] !== memeCount || $[29] !== selectedType || $[30] !== setCreditNfts) {
    t11 = selectedType === ApiWaveCreditType.CardSetTdh && <MemeCardSetPicker creditNfts={creditNfts} memeCount={memeCount} isMemeCountLoading={isMemeCountLoading} isMemeCountError={isMemeCountError} errors={errors} onCreditNftsChange={setCreditNfts} />;
    $[24] = creditNfts;
    $[25] = errors;
    $[26] = isMemeCountError;
    $[27] = isMemeCountLoading;
    $[28] = memeCount;
    $[29] = selectedType;
    $[30] = setCreditNfts;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t10 || $[33] !== t11 || $[34] !== t9) {
    t12 = <div className={VOTING_OPTIONS_GRID_CLASSES}>{t9}{t10}{t11}</div>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t9;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== creditScope || $[37] !== onCreditScopeChange) {
    t13 = <CreateWaveCreditScopeSelect creditScope={creditScope} onCreditScopeChange={onCreditScopeChange} />;
    $[36] = creditScope;
    $[37] = onCreditScopeChange;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== approvalThreshold || $[40] !== approvalThresholdError || $[41] !== errors || $[42] !== maxVotesPerIdentityPerDrop || $[43] !== setApprovalThreshold || $[44] !== setMaxVotesPerIdentityPerDrop || $[45] !== showVotingSettings || $[46] !== waveType) {
    t14 = showVotingSettings && <div data-testid="create-wave-voting-settings-grid" className={VOTING_SETTINGS_GRID_CLASSES}><MaxVotesPerIdentityInput value={maxVotesPerIdentityPerDrop} errors={errors} onChange={setMaxVotesPerIdentityPerDrop} />{waveType === ApiWaveType.Approve && <CreateWaveVotingThreshold threshold={approvalThreshold} error={approvalThresholdError} setThreshold={setApprovalThreshold} />}</div>;
    $[39] = approvalThreshold;
    $[40] = approvalThresholdError;
    $[41] = errors;
    $[42] = maxVotesPerIdentityPerDrop;
    $[43] = setApprovalThreshold;
    $[44] = setMaxVotesPerIdentityPerDrop;
    $[45] = showVotingSettings;
    $[46] = waveType;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  let t15;
  if ($[48] !== approvalHoldMode || $[49] !== approvalThresholdTimeErrorMessage || $[50] !== approvalThresholdTimeMs || $[51] !== onApprovalHoldModeChange || $[52] !== onApprovalThresholdTimeChange || $[53] !== onTimeWeightedChange || $[54] !== timeWeighted || $[55] !== timeWeightedErrorMessage || $[56] !== waveType) {
    t15 = waveType === ApiWaveType.Approve && <><TimeWeightedVoting config={timeWeighted} errorMessage={timeWeightedErrorMessage} onChange={onTimeWeightedChange} /><CreateWaveApprovalHold selectedMode={approvalHoldMode} onModeChange={onApprovalHoldModeChange} />{approvalHoldMode === CreateWaveApprovalHoldMode.HOLD && <div className="tw-mt-3" data-testid="approval-hold-detail"><CreateWaveVotingThresholdTime thresholdTimeMs={approvalThresholdTimeMs} errorMessage={approvalThresholdTimeErrorMessage} usesTimeWeightedScore={timeWeighted.enabled} setThresholdTimeMs={onApprovalThresholdTimeChange} /></div>}</>;
    $[48] = approvalHoldMode;
    $[49] = approvalThresholdTimeErrorMessage;
    $[50] = approvalThresholdTimeMs;
    $[51] = onApprovalHoldModeChange;
    $[52] = onApprovalThresholdTimeChange;
    $[53] = onTimeWeightedChange;
    $[54] = timeWeighted;
    $[55] = timeWeightedErrorMessage;
    $[56] = waveType;
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  let t16;
  if ($[58] !== allowNegativeVotes || $[59] !== onAllowNegativeVotesChange || $[60] !== waveType) {
    t16 = waveType !== ApiWaveType.Chat && <NegativeVotingToggle allowNegativeVotes={allowNegativeVotes} onChange={onAllowNegativeVotesChange} isDisabled={false} />;
    $[58] = allowNegativeVotes;
    $[59] = onAllowNegativeVotesChange;
    $[60] = waveType;
    $[61] = t16;
  } else {
    t16 = $[61];
  }
  let t17;
  if ($[62] !== onTimeWeightedChange || $[63] !== timeWeighted || $[64] !== timeWeightedErrorMessage || $[65] !== waveType) {
    t17 = waveType === ApiWaveType.Rank && <TimeWeightedVoting config={timeWeighted} errorMessage={timeWeightedErrorMessage} onChange={onTimeWeightedChange} />;
    $[62] = onTimeWeightedChange;
    $[63] = timeWeighted;
    $[64] = timeWeightedErrorMessage;
    $[65] = waveType;
    $[66] = t17;
  } else {
    t17 = $[66];
  }
  let t18;
  if ($[67] !== t12 || $[68] !== t13 || $[69] !== t14 || $[70] !== t15 || $[71] !== t16 || $[72] !== t17 || $[73] !== t8) {
    t18 = <div>{t8}{t12}{t13}{t14}{t15}{t16}{t17}</div>;
    $[67] = t12;
    $[68] = t13;
    $[69] = t14;
    $[70] = t15;
    $[71] = t16;
    $[72] = t17;
    $[73] = t8;
    $[74] = t18;
  } else {
    t18 = $[74];
  }
  return t18;
}
function _temp(votingType) {
  return VOTING_TYPES_ORDER[votingType] !== undefined;
}